import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import axios, { AxiosError } from 'axios';
import { getApiHost } from '@/appSettings';
import { useNotificationStore } from '@/store/notificationStore';
import { NotificationStates } from '@/models/enums/NotificationStates';
import { Guid } from 'guid-typescript';
import { i18n } from '@/i18n';
import { useOpentelemetry } from '@/composables/useOpentelemetry';
import { SpanStatusCode, type Span } from '@opentelemetry/api';

type RequestData = {
    instance: AxiosInstance,
    url: string,
    postData: object
};

type Header = { [key: string]: string; };

interface AxiosRequestConfigWithSpan extends InternalAxiosRequestConfig {
    _otelSpan?: Span;
    _otelStartTime?: number;
}

export class RestManager {
    /** Address to the service we want to call */
    private readonly serviceAdress: string;

    private controller = new AbortController();

    public constructor(
        serviceAdress: string
    ) {
        if (!serviceAdress.startsWith('http')) {
            // If the serviceAdress does not start with http, we expect it to be an api service and wrap it in the api service address.
            serviceAdress = `https://${getApiHost()}/${serviceAdress}`;
        }
        this.serviceAdress = serviceAdress;
    }

    /**
     * Sends a POST to the endpoint function, and returns its data.
     * @param data The data (if any) to request.
     * @param method Method to run if the url is an api with multiple methods.
     * @param customHeaders Object containing any header values that are specific to this call.
     */
    public async post<T>(
        data?: unknown,
        method?: string,
        customHeaders?: Header,
        options?: AxiosRequestConfig,
        contentType?: string,
        local?: string
    ): Promise<T> {
        const request = this.prepareRequest(data, method, customHeaders, options, contentType, local);
        try {
            const result = await request.instance.post<T>(request.url, request.postData);
            return result.data;
        } catch (error: unknown) {
            if (error instanceof AxiosError && error.response?.status === 403) {
                console.error('USER NOT AUTHENTICATED', request.url);
                useNotificationStore().add({
                    id: Guid.create(),
                    state: NotificationStates.Warning,
                    message: i18n.global.t('system.notAllowed'),
                    created: new Date(),
                    isPreviewHidden: false
                });
            }

            throw (error);
        }
    }

    /**
     * Sends a GET to the endpoint function, and returns its data.
     * @param method Method to run if the url is an api with multiple methods.
     * @param customHeaders Object containing any header values that are specific to this call.
     */
    public async get<T>(
        method?: string,
        customHeaders?: Header,
        options?: AxiosRequestConfig,
        local?: string
    ): Promise<T> {
        const request = this.prepareRequest(null, method, customHeaders, options, undefined, local);

        try {
            const result = await request.instance.get<T>(request.url);
            return result.data;
        } catch (error: unknown) {
            if (error instanceof AxiosError && error.response?.status === 403) {
                console.error('USER NOT AUTHENTICATED', request.url);
                useNotificationStore().add({
                    id: Guid.create(),
                    state: NotificationStates.Warning,
                    message: i18n.global.t('system.notAllowed'),
                    created: new Date(),
                    isPreviewHidden: false
                });
            }

            throw (error);
        }
    }

    /**
     * Sends a PUT to the endpoint function, and returns its data.
     * @param data The data (if any) to request.
     * @param method Method to run if the url is an api with multiple methods.
     * @param customHeaders Object containing any header values that are specific to this call.
     */
    public async put<T>(
        data?: unknown,
        method?: string,
        customHeaders?: Header,
        options?: AxiosRequestConfig,
        local?: string
    ): Promise<T> {
        const request = this.prepareRequest(data, method, customHeaders, options, undefined, local);

        try {
            const result = await request.instance.put<T>(request.url, request.postData);
            return result.data;
        } catch (error: unknown) {
            if (error instanceof AxiosError && error.response?.status === 403) {
                console.error('USER NOT AUTHENTICATED', request.url);
                useNotificationStore().add({
                    id: Guid.create(),
                    state: NotificationStates.Warning,
                    message: i18n.global.t('system.notAllowed'),
                    created: new Date(),
                    isPreviewHidden: false
                });
            }

            throw (error);
        }
    }

    /**
     * Sends a DELETE to the endpoint function, and returns its data.
     * @param method Method to run if the url is an api with multiple methods.
     * @param customHeaders Object containing any header values that are specific to this call.
     */
    public async delete<T>(
        method?: string,
        customHeaders?: Header,
        options?: AxiosRequestConfig,
        local?: string
    ): Promise<T> {
        const request = this.prepareRequest(null, method, customHeaders, options, undefined, local);

        try {
            const result = await request.instance.delete<T>(request.url);
            return result.data;
        } catch (error: unknown) {
            if (error instanceof AxiosError && error.response?.status === 403) {
                console.error('USER NOT AUTHENTICATED', request.url);
                useNotificationStore().add({
                    id: Guid.create(),
                    state: NotificationStates.Warning,
                    message: i18n.global.t('system.notAllowed'),
                    created: new Date(),
                    isPreviewHidden: false
                });
            }

            throw (error);
        }
    }

    /**
     * Creates a request object to send axios.
     * @param data The data (if any) to send to the method.
     * @param method Method to run if the url is an api with multiple methods.
     * @param customHeaders Object containing any header values that are specific to this call.
     */
    private prepareRequest(
        data?: unknown,
        method?: string,
        customHeaders?: Header,
        options?: AxiosRequestConfig,
        contentType?: string,
        local?: string
    ): RequestData {
        // Get the url to the API from the settings
        let url: string = this.serviceAdress;

        if (local != null) {
            url = local;
        }

        if (method != null) {
            url += `/${method}`;
        }

        // If the data is null or undefined, use empty object
        const postData = data ?? {};

        // Create an instance of Axios
        const instance = axios.create({...options, withCredentials: true });

        instance.interceptors.request.use(request => {
            request.headers.set('Content-Type', contentType ?? 'application/json');

            if (customHeaders != null) {
                for (const key of Object.keys(customHeaders)) {
                    request.headers.set(key, customHeaders[key]);
                }
            }

            if (this.controller != null && request.signal == null) {
                request.signal = this.controller.signal;
            }

            // Start OpenTelemetry span for HTTP request
            const { getTracer } = useOpentelemetry();
            const tracer = getTracer();
            const requestConfig = request as AxiosRequestConfigWithSpan;
            const span = tracer.startSpan(`HTTP ${request.method?.toUpperCase() ?? 'REQUEST'}`);
            span.setAttribute('http.method', request.method?.toUpperCase() ?? 'UNKNOWN');
            span.setAttribute('http.url', request.url ?? '');
            requestConfig._otelSpan = span;
            requestConfig._otelStartTime = performance.now();

            return request;
        });

        // Response interceptor to end the span
        instance.interceptors.response.use(
            response => {
                const requestConfig = response.config as AxiosRequestConfigWithSpan;
                const span = requestConfig._otelSpan;
                if (span != null) {
                    span.setAttribute('http.status_code', response.status);
                    span.setStatus({ code: SpanStatusCode.OK });
                    if (requestConfig._otelStartTime != null) {
                        span.setAttribute('http.duration_ms', performance.now() - requestConfig._otelStartTime);
                    }
                    span.end();
                }
                return response;
            },
            async error => {
                if (error instanceof AxiosError && error.config != null) {
                    const requestConfig = error.config as AxiosRequestConfigWithSpan;
                    const span = requestConfig._otelSpan;
                    if (span != null) {
                        span.setAttribute('http.status_code', error.response?.status ?? 0);
                        span.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
                        span.recordException(error);
                        if (requestConfig._otelStartTime != null) {
                            span.setAttribute('http.duration_ms', performance.now() - requestConfig._otelStartTime);
                        }
                        span.end();
                    }
                }
                // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                return Promise.reject(error);
            }
        );

        // Return the values
        return {
            instance,
            url,
            postData
        };
    }

    public abort(): void {
        this.controller.abort();
        this.resetAbort();
    }

    public resetAbort(): void {
        this.controller = new AbortController();
    }
}
